import {
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';
import FocusLock from 'react-focus-lock';

import { PopoverMenuItem, PopoverMenuList } from '@/components/PopoverMenu';
import { SelectButton } from '@/components/SelectButton';

export interface MenuSelectOption {
  label: string;
  value: string;
}

interface MenuSelectProps extends Omit<PopoverProps, 'children'> {
  value: string;
  onChange: (value: string) => void;
  options: MenuSelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
  maxHeight?: number | string;
}

export const MenuSelect: React.FC<MenuSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  isDisabled,
  maxHeight = 56,
  ...popoverProps
}) => {
  const fontSize = useBreakpointValue({
    base: 'xs',
    md: 'sm',
  });
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Popover matchWidth {...popoverProps}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <SelectButton
              fontSize={fontSize}
              isDisabled={isDisabled}
              w="full"
              justifyContent="flex-start"
            >
              {selectedOption?.label || placeholder}
            </SelectButton>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              width="full"
              shadow="lg"
              maxHeight={maxHeight}
              overflow="auto"
            >
              <FocusLock returnFocus persistentFocus={false}>
                <PopoverMenuList>
                  {options.map((opt) => (
                    <PopoverMenuItem
                      key={opt.value}
                      onClick={() => {
                        // close the popover
                        onClose();
                        onChange(opt.value);
                      }}
                      isChecked={opt.value === value}
                    >
                      {opt.label}
                    </PopoverMenuItem>
                  ))}
                </PopoverMenuList>
              </FocusLock>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

export default MenuSelect;
